/**
 * Fixed-timestep game loop with a real-time accumulator.
 *
 * Each animation frame:
 *  - measure dt since last frame
 *  - while accumulator >= TICK_MS, run one sim step and decrement
 *  - render once (last render reflects latest state)
 *
 * Spiral-of-death guard: if the accumulator exceeds MAX_CATCHUP_TICKS worth
 * of work, we drop the excess. Better to drop frames than to hang the tab.
 */

import type { GameState, InputFrame } from '../sim/types.ts';

export const TICKS_PER_SECOND = 60;
export const TICK_MS = 1000 / TICKS_PER_SECOND;
const MAX_CATCHUP_TICKS = 5;

export type LoopHandle = {
  stop(): void;
  getState(): GameState;
};

export type LoopOptions = {
  initial: GameState;
  step: (state: GameState, input: InputFrame, ticks: number) => GameState;
  pollInput: () => InputFrame;
  render: (state: GameState) => void;
  /** Called after each sim tick — wire audio/event consumers here. */
  onAfterStep?: (state: GameState) => void;
  /** Optional: pause skips sim steps but still renders. Defaults to never paused. */
  isPaused?: () => boolean;
  /** Override for testing. Defaults to performance.now and requestAnimationFrame. */
  now?: () => number;
  raf?: (cb: (t: number) => void) => number;
  cancel?: (id: number) => void;
};

export function startLoop(opts: LoopOptions): LoopHandle {
  const now = opts.now ?? (() => performance.now());
  const raf = opts.raf ?? ((cb) => requestAnimationFrame(cb));
  const cancel = opts.cancel ?? ((id) => cancelAnimationFrame(id));

  let state = opts.initial;
  let acc = 0;
  let last = now();
  let stopped = false;
  let rafId = 0;

  const isPaused = opts.isPaused ?? (() => false);

  function frame(t: number): void {
    if (stopped) return;
    const dt = t - last;
    last = t;
    if (!isPaused()) {
      acc = Math.min(acc + dt, MAX_CATCHUP_TICKS * TICK_MS);

      // Integer-floor avoids float drift: do exactly Math.floor(acc / TICK_MS)
      // sim steps, then subtract their exact total. Capped to MAX_CATCHUP_TICKS.
      const wanted = Math.floor(acc / TICK_MS);
      const ticks = Math.min(wanted, MAX_CATCHUP_TICKS);
      for (let i = 0; i < ticks; i++) {
        const input = opts.pollInput();
        state = opts.step(state, input, 1);
        opts.onAfterStep?.(state);
      }
      acc -= ticks * TICK_MS;
    } else {
      // Paused: drop accumulated dt so we don't fast-forward on resume.
      acc = 0;
    }

    opts.render(state);
    rafId = raf(frame);
  }

  rafId = raf(frame);

  return {
    stop(): void { stopped = true; cancel(rafId); },
    getState(): GameState { return state; },
  };
}
